import { Box, useColorMode } from '@chakra-ui/react';
import { ChakraStylesConfig, Props } from 'chakra-react-select';
import AutoComplete from '../auto-complete';
import Text from '../text';

// A thin wrapper around the shared <AutoComplete> that adds a floating uppercase
// label on a color-mode-aware card surface. It does NOT modify AutoComplete —
// unrelated pages that use AutoComplete directly are unaffected.
interface LabeledSelectProps extends Props {
    label: string;
    colorMode: string | undefined;
}

export default function LabeledSelect({ label, ...rest }: LabeledSelectProps) {
    const { colorMode } = useColorMode();
    const isLight = colorMode === 'light';

    // Blend the inner select into the card: strip its own border/background so
    // the card is the single visual container, while keeping the placeholder
    // color logic AutoComplete normally applies and the chevron indicator.
    const chakraStyles: ChakraStylesConfig = {
        container: (provided) => ({
            ...provided,
            cursor: 'pointer',
            textAlign: 'left',
        }),
        control: (provided) => ({
            ...provided,
            bg: 'transparent',
            border: 'none',
            boxShadow: 'none',
            minH: 'auto',
            px: 0,
        }),
        placeholder: (provided) => ({
            ...provided,
            color: isLight ? 'navy' : 'white',
            textAlign: 'left',
        }),
        indicatorSeparator: (provided) => ({
            ...provided,
            display: 'none',
        }),
    };

    return (
        <Box
            w="full"
            bg={isLight ? 'white' : 'darkNavy'}
            borderWidth={1}
            borderStyle="solid"
            borderColor={isLight ? 'border' : '#0E1A56'}
            borderRadius={14}
            px="16px"
            pt="12px"
            pb="6px"
            textAlign="left"
        >
            <Text
                size="xs"
                letterSpacing="0.08em"
                textTransform="uppercase"
                opacity={0.6}
                mb="2px"
                color={isLight ? 'midnightBlue' : 'white'}
            >
                {label}
            </Text>
            <AutoComplete chakraStyles={chakraStyles} {...(rest as any)} />
        </Box>
    );
}
